import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, Navigate } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  CreditCard,
  Landmark,
} from "lucide-react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_CUSTOMER = {
  name: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  postal_code: "",
  country: "ES",
  notes: "",
};

const labelStyle = (err) =>
  `text-[10px] uppercase tracking-[0.28em] ${err ? "text-red-700" : "text-olive-800/60"}`;
const inputStyle = (err) =>
  `w-full bg-cream-100 border ${err ? "border-red-700" : "border-cream-400"} px-4 py-3 text-olive-900 text-sm font-light placeholder:text-olive-800/40 focus:outline-none focus:border-olive-700 transition-colors`;

const euro = (n) => (n || 0).toFixed(2).replace(".", ",") + " €";

// -------------------- stepper --------------------
function Stepper({ step, t }) {
  const labels = [
    t("checkout_page.step_data") || "Datos",
    t("checkout_page.step_payment") || "Pago",
    t("checkout_page.step_confirm") || "Confirmación",
  ];
  return (
    <ol
      data-testid="checkout-stepper"
      className="mt-6 flex items-center gap-2 sm:gap-4 text-[10px] sm:text-[11px] uppercase tracking-[0.24em]"
    >
      {labels.map((l, i) => {
        const idx = i + 1;
        const done = idx < step;
        const active = idx === step;
        return (
          <li
            key={l}
            data-testid={`stepper-step-${idx}`}
            className={`flex items-center gap-2 ${
              active ? "text-olive-900" : done ? "text-olive-700" : "text-olive-800/40"
            }`}
          >
            <span
              className={`w-6 h-6 grid place-items-center rounded-full text-[10px] ${
                active
                  ? "bg-olive-700 text-cream-100"
                  : done
                  ? "bg-gold-deep text-olive-900"
                  : "bg-cream-300 text-olive-800/60"
              }`}
            >
              {done ? <Check size={12} /> : idx}
            </span>
            <span className="hidden sm:inline font-medium">{l}</span>
            {idx < 3 && <span className="mx-2 sm:mx-3 text-olive-800/30">—</span>}
          </li>
        );
      })}
    </ol>
  );
}

// -------------------- main page --------------------
export default function CheckoutPage() {
  const { t, lang } = useLang();
  const { items, totals, clearCart } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(INITIAL_CUSTOMER);
  const [payment, setPayment] = useState("stripe");
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [topError, setTopError] = useState(null);
  const [orderResult, setOrderResult] = useState(null); // {order_number, total, payment_method, checkout_url, bank}

  // Load shipping quote whenever cart changes
  useEffect(() => {
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    axios
      .post(`${API}/shipping/quote`, {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
      })
      .then((res) => setQuote(res.data))
      .catch(() => setQuote(null));
  }, [items]);

  const shipping = quote?.shipping || 0;
  const total = quote?.total || totals.subtotal;

  const stepCtaLabel = useMemo(() => {
    if (submitting) return t("checkout_page.processing");
    if (payment === "stripe") return t("checkout_page.place_order_stripe");
    return t("checkout_page.place_order_transfer");
  }, [payment, submitting, t]);

  // Block all checkout/purchase flows in pre-harvest mode (after all hooks)
  if (PRE_HARVEST_MODE) {
    return <Navigate to={lang === "en" ? "/en" : "/"} replace />;
  }

  // Empty cart shortcut (only blocks step 1; step 3 should still render the result)

  const validateData = () => {
    const e = {};
    const req = ["name", "email", "phone", "address1", "city", "province", "postal_code"];
    req.forEach((k) => {
      if (!customer[k] || customer[k].trim().length < 2) e[k] = true;
    });
    if (!/^\S+@\S+\.\S+$/.test(customer.email || "")) e.email = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goToPayment = () => {
    setTopError(null);
    if (items.length === 0) {
      setTopError(t("checkout_page.cart_empty"));
      return;
    }
    if (!validateData()) {
      setTopError(t("checkout_page.missing_field"));
      return;
    }
    if (!terms) {
      setTopError(t("checkout_page.must_accept"));
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const placeOrder = async () => {
    setTopError(null);
    if (!terms) {
      setTopError(t("checkout_page.must_accept"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        customer,
        lang,
        payment_method: payment,
        accept_terms: true,
        origin_url: window.location.origin,
      });
      const data = res.data;
      setOrderResult(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setTopError(typeof detail === "string" ? detail : t("common.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const continueToStripe = () => {
    if (orderResult?.checkout_url) {
      clearCart();
      window.location.href = orderResult.checkout_url;
    }
  };

  const goToTransferPage = () => {
    if (orderResult?.order_number) {
      const email = customer.email ? `&email=${encodeURIComponent(customer.email)}` : "";
      // Persist the transfer details so a page refresh on /pedido/transferencia
      // still shows the real IBAN (router state alone doesn't survive reload).
      try {
        sessionStorage.setItem(
          `csm_transfer_${orderResult.order_number}`,
          JSON.stringify({
            bank: orderResult.bank,
            total: orderResult.total,
            currency: orderResult.currency || "EUR",
          })
        );
      } catch {
        /* sessionStorage may be blocked — router state still carries the data */
      }
      clearCart();
      navigate(`/pedido/transferencia?order_number=${orderResult.order_number}${email}`, {
        state: {
          bank: orderResult.bank,
          total: orderResult.total,
          currency: orderResult.currency || "EUR",
        },
      });
    }
  };

  // Empty cart shortcut (only blocks step 1; step 3 should still render the result)
  if (items.length === 0 && step !== 3) {
    return (
      <main className="min-h-screen bg-cream-100">
        <ShopNav />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-3xl text-olive-900">{t("cart_page.empty_title")}</h1>
          <Link
            to="/tienda"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em]"
          >
            {t("cart_page.go_shop")} <ArrowRight size={14} />
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="min-h-screen bg-cream-100 text-charcoal">
      <ShopNav />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 sm:py-16">
        <h1 className="font-serif text-4xl sm:text-5xl text-olive-900">{t("checkout_page.title")}</h1>
        <p className="mt-3 text-olive-800/80 font-light">{t("checkout_page.subtitle")}</p>
        <Stepper step={step} t={t} />

        {topError && (
          <div data-testid="checkout-error" className="mt-6 p-4 border border-red-700/40 bg-red-50 text-red-800 text-sm">
            {topError}
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            {/* ---------------- STEP 1: Datos ---------------- */}
            {step === 1 && (
              <div data-testid="checkout-step-1" className="space-y-10">
                <div className="border border-cream-400 p-6 sm:p-8 bg-cream-100">
                  <h2 className="font-serif text-2xl text-olive-900">{t("checkout_page.customer_title")}</h2>
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelStyle(errors.name)}>{t("checkout_page.name")}</label>
                      <input data-testid="ck-name" className={inputStyle(errors.name)} value={customer.name} onChange={set("name")} />
                    </div>
                    <div>
                      <label className={labelStyle(errors.email)}>{t("checkout_page.email")}</label>
                      <input data-testid="ck-email" type="email" className={inputStyle(errors.email)} value={customer.email} onChange={set("email")} />
                    </div>
                    <div>
                      <label className={labelStyle(errors.phone)}>{t("checkout_page.phone")}</label>
                      <input data-testid="ck-phone" className={inputStyle(errors.phone)} value={customer.phone} onChange={set("phone")} />
                    </div>
                  </div>
                </div>

                <div className="border border-cream-400 p-6 sm:p-8 bg-cream-100">
                  <h2 className="font-serif text-2xl text-olive-900">{t("checkout_page.address_title")}</h2>
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                      <label className={labelStyle(errors.address1)}>{t("checkout_page.address1")}</label>
                      <input data-testid="ck-address1" className={inputStyle(errors.address1)} value={customer.address1} onChange={set("address1")} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle(false)}>{t("checkout_page.address2")}</label>
                      <input data-testid="ck-address2" className={inputStyle(false)} value={customer.address2 || ""} onChange={set("address2")} />
                    </div>
                    <div>
                      <label className={labelStyle(errors.city)}>{t("checkout_page.city")}</label>
                      <input data-testid="ck-city" className={inputStyle(errors.city)} value={customer.city} onChange={set("city")} />
                    </div>
                    <div>
                      <label className={labelStyle(errors.province)}>{t("checkout_page.province")}</label>
                      <input data-testid="ck-province" className={inputStyle(errors.province)} value={customer.province} onChange={set("province")} />
                    </div>
                    <div>
                      <label className={labelStyle(errors.postal_code)}>{t("checkout_page.postal_code")}</label>
                      <input data-testid="ck-postal" className={inputStyle(errors.postal_code)} value={customer.postal_code} onChange={set("postal_code")} />
                    </div>
                    <div>
                      <label className={labelStyle(false)}>{t("checkout_page.country")}</label>
                      <input
                        data-testid="ck-country"
                        className={`${inputStyle(false)} opacity-70 cursor-not-allowed`}
                        value="España"
                        readOnly
                        aria-readonly="true"
                      />
                      <p
                        data-testid="ck-country-ppwr-note"
                        className="mt-1.5 text-[11px] text-olive-800/70 leading-snug"
                      >
                        {t("checkout_page.country_locked_note")}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelStyle(false)}>{t("checkout_page.notes")}</label>
                      <textarea data-testid="ck-notes" rows={3} className={inputStyle(false)} value={customer.notes || ""} onChange={set("notes")} />
                    </div>
                  </div>
                </div>

                <div data-testid="ck-terms-block" className="border border-cream-400 p-6 sm:p-8 bg-cream-100">
                  <label className="flex items-start gap-3 text-sm text-olive-800/85 font-light cursor-pointer">
                    <input
                      data-testid="ck-terms"
                      type="checkbox"
                      checked={terms}
                      onChange={(e) => setTerms(e.target.checked)}
                      className="mt-1 accent-olive-700 w-4 h-4 shrink-0"
                    />
                    <span className="leading-relaxed">
                      {t("checkout_page.terms_prefix")}
                      <Link
                        to="/legal/aviso"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-gold underline-offset-4 hover:text-olive-900"
                      >
                        {t("checkout_page.terms_link_aviso")}
                      </Link>
                      {t("checkout_page.terms_and")}
                      <Link
                        to="/legal/privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-gold underline-offset-4 hover:text-olive-900"
                      >
                        {t("checkout_page.terms_link_privacy")}
                      </Link>
                      {t("checkout_page.terms_suffix")}
                    </span>
                  </label>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Link
                    to="/carrito"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:text-olive-900"
                  >
                    <ArrowLeft size={14} /> Volver al carrito
                  </Link>
                  <button
                    type="button"
                    onClick={goToPayment}
                    disabled={!terms}
                    aria-disabled={!terms}
                    data-testid="ck-step1-next"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 disabled:bg-olive-700/40 disabled:cursor-not-allowed disabled:hover:bg-olive-700/40"
                  >
                    Continuar al pago <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* ---------------- STEP 2: Pago ---------------- */}
            {step === 2 && (
              <div data-testid="checkout-step-2" className="space-y-10">
                <div className="border border-cream-400 p-6 sm:p-8 bg-cream-100">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">Datos del pedido</p>
                      <p className="mt-2 font-serif text-olive-900">{customer.name} · {customer.email}</p>
                      <p className="text-sm text-olive-800/80">
                        {customer.address1}{customer.address2 ? `, ${customer.address2}` : ""} · {customer.postal_code} {customer.city} ({customer.province}) · {customer.country}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      data-testid="ck-step2-edit-data"
                      className="text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:text-olive-900 underline underline-offset-4"
                    >
                      Editar
                    </button>
                  </div>
                </div>

                <div className="border border-cream-400 p-6 sm:p-8 bg-cream-100">
                  <h2 className="font-serif text-2xl text-olive-900">{t("checkout_page.payment_title")}</h2>
                  <div className="mt-6 space-y-3">
                    <label
                      data-testid="payment-stripe"
                      className={`flex items-start gap-4 p-5 border cursor-pointer transition-colors ${
                        payment === "stripe" ? "border-olive-700 bg-cream-200" : "border-cream-400 hover:bg-cream-200/50"
                      }`}
                    >
                      <input type="radio" name="pay" value="stripe" checked={payment === "stripe"} onChange={() => setPayment("stripe")} className="mt-1" />
                      <CreditCard size={20} strokeWidth={1.3} className="text-gold-deep mt-0.5" />
                      <div className="flex-1">
                        <div className="font-serif text-olive-900 text-lg">{t("checkout_page.payment_stripe")}</div>
                        <div className="text-sm text-olive-800/70 font-light">
                          {t("checkout_page.payment_stripe_desc")}
                        </div>
                      </div>
                    </label>
                    <div
                      data-testid="payment-paypal-disabled"
                      className="flex items-start gap-4 p-5 border border-cream-400 opacity-50 cursor-not-allowed"
                      title="coming soon"
                    >
                      <input type="radio" disabled className="mt-1" />
                      <div className="flex-1">
                        <div className="font-serif text-olive-900 text-lg">{t("checkout_page.payment_paypal")}</div>
                        <div className="text-sm text-olive-800/70 font-light">{t("checkout_page.payment_paypal_desc")}</div>
                      </div>
                    </div>
                    <label
                      data-testid="payment-transfer"
                      className={`flex items-start gap-4 p-5 border cursor-pointer transition-colors ${
                        payment === "transfer" ? "border-olive-700 bg-cream-200" : "border-cream-400 hover:bg-cream-200/50"
                      }`}
                    >
                      <input type="radio" name="pay" value="transfer" checked={payment === "transfer"} onChange={() => setPayment("transfer")} className="mt-1" />
                      <Landmark size={20} strokeWidth={1.3} className="text-gold-deep mt-0.5" />
                      <div className="flex-1">
                        <div className="font-serif text-olive-900 text-lg">{t("checkout_page.payment_transfer")}</div>
                        <div className="text-sm text-olive-800/70 font-light">{t("checkout_page.payment_transfer_desc")}</div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    data-testid="ck-step2-back"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:text-olive-900"
                  >
                    <ArrowLeft size={14} /> Volver a datos
                  </button>
                  <button
                    type="button"
                    onClick={placeOrder}
                    disabled={submitting}
                    data-testid="place-order-button"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 disabled:opacity-60"
                  >
                    {stepCtaLabel} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* ---------------- STEP 3: Confirmación ---------------- */}
            {step === 3 && orderResult && (
              <div data-testid="checkout-step-3" className="border border-cream-400 p-6 sm:p-10 bg-cream-100">
                <div className="flex items-start gap-4">
                  <CheckCircle size={32} strokeWidth={1.2} className="text-gold-deep shrink-0" />
                  <div className="flex-1">
                    <h2 className="font-serif text-3xl text-olive-900">¡Pedido creado!</h2>
                    <p className="mt-2 text-olive-800/80 font-light">
                      Hemos registrado tu pedido <strong className="text-olive-900">{orderResult.order_number}</strong>.
                      {orderResult.payment_method === "stripe"
                        ? " Te llevamos a la pasarela de pago segura de Stripe para completar el cobro."
                        : " Reservamos tu pedido durante 72 h. A continuación te mostramos los datos para hacer la transferencia."}
                    </p>
                    <p className="mt-1 text-olive-800/60 text-sm">
                      Recibirás un email de confirmación en <strong>{customer.email}</strong>.
                    </p>
                  </div>
                </div>

                <dl className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Número de pedido</dt>
                    <dd className="mt-1 font-serif text-olive-900 text-lg" data-testid="ck-order-number">
                      {orderResult.order_number}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Total</dt>
                    <dd className="mt-1 font-serif text-olive-900 text-lg">{euro(orderResult.total)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Método de pago</dt>
                    <dd className="mt-1 text-olive-900">
                      {orderResult.payment_method === "stripe" ? "Tarjeta (Stripe)" : "Transferencia bancaria"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">Estado</dt>
                    <dd className="mt-1 text-olive-900">
                      {orderResult.payment_method === "stripe"
                        ? "Pendiente de pago"
                        : "Pendiente de transferencia"}
                    </dd>
                  </div>
                </dl>

                <div className="mt-10 flex flex-wrap items-center gap-3">
                  {orderResult.payment_method === "stripe" ? (
                    <button
                      type="button"
                      onClick={continueToStripe}
                      data-testid="ck-continue-stripe"
                      className="inline-flex items-center gap-2 px-6 py-3.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800"
                    >
                      Pagar ahora con Stripe <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={goToTransferPage}
                      data-testid="ck-continue-transfer"
                      className="inline-flex items-center gap-2 px-6 py-3.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800"
                    >
                      Ver datos de la transferencia <ArrowRight size={14} />
                    </button>
                  )}
                  <Link
                    to="/tienda"
                    className="text-[11px] uppercase tracking-[0.24em] text-olive-800 hover:text-olive-900 underline underline-offset-4"
                  >
                    Seguir explorando
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Summary aside (sticky) */}
          {step !== 3 && (
            <aside className="lg:col-span-4">
              <div className="border border-cream-400 bg-cream-200 p-6 sm:p-8 lg:sticky lg:top-24">
                <h2 className="font-serif text-2xl text-olive-900">{t("checkout_page.summary_title")}</h2>
                <ul className="mt-5 space-y-3 text-sm">
                  {items.map((it) => (
                    <li key={it.product_id} className="flex justify-between gap-3 pb-3 border-b border-cream-400">
                      <span className="text-olive-900 font-light">
                        {lang === "es" ? it.name_es : it.name_en} × {it.qty}
                      </span>
                      <span className="font-serif text-olive-900">{euro(it.price * it.qty)}</span>
                    </li>
                  ))}
                </ul>
                <dl className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-olive-800/80 font-light">{t("cart_page.subtotal")}</dt>
                    <dd className="font-serif text-olive-900">{euro(totals.subtotal)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-olive-800/80 font-light">{t("cart_page.shipping")}</dt>
                    <dd className="font-serif text-olive-900">{euro(shipping)}</dd>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-cream-400">
                    <dt className="font-serif text-olive-900 text-lg">{t("cart_page.total")}</dt>
                    <dd data-testid="checkout-total" className="font-serif text-olive-900 text-xl">
                      {euro(total)}
                    </dd>
                  </div>
                </dl>
              </div>
            </aside>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
